import {
  cloneElement,
  isValidElement,
  lazy,
  Suspense,
  useLayoutEffect,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/app/navigation/use-navigation';
import type {
  OverlayDrawerKind,
  OverlayDrawerState,
} from '@/app/navigation/navigation-state';
import {
  resolveCanalRecurso,
  resolveDrawerPermissions,
  type HasPermissionFn,
} from '@/app/routing/drawer-permissions';

// JOSH-91: notice shown instead of a drawer whose view is on the other channel.
const AvisoOutroCanalDrawer = lazy(
  () => import('@/features/auth/components/AvisoOutroCanal/AvisoOutroCanalDrawer'),
);

const ObraDrawer = lazy(() => import('@/features/obra/components/ObraDrawer'));
const ObraJornadaDrawer = lazy(() => import('@/features/obra/components/ObraJornadaDrawer'));
const ProjetoDrawer = lazy(() => import('@/features/projetos/components/ProjetoDrawer'));
const AtividadeDrawer = lazy(() => import('@/features/atividade/components/AtividadeDrawer'));
const OportunidadeDrawer = lazy(() => import('@/features/comercial/components/OportunidadeDrawer'));
const RequisicaoDrawer = lazy(() => import('@/features/suprimentos/components/RequisicaoDrawer'));
const OrdemCompraDrawer = lazy(() => import('@/features/suprimentos/components/OrdemCompraDrawer'));
const PessoaDrawer = lazy(() => import('@/features/pessoa/components/PessoaDrawer'));
const OsDrawer = lazy(() => import('@/features/os/components/OsDrawer'));
const OsServicoDrawer = lazy(() => import('@/features/os/components/OsServicoDrawer'));
const OsVistoriaDrawer = lazy(() => import('@/features/os/components/OsVistoriaDrawer'));
const OsReportReviewDrawer = lazy(() => import('@/features/os/components/OsReportReviewDrawer'));
const OsRequisicaoDrawer = lazy(() => import('@/features/suprimentos/components/OsRequisicaoDrawer'));
const OrcamentoDrawer = lazy(() => import('@/features/suprimentos/components/OrcamentoDrawer'));
const MovimentacaoEstoqueDrawer = lazy(() => import('@/features/suprimentos/components/MovimentacaoEstoqueDrawer'));
const CATDrawer = lazy(() => import('@/features/suprimentos/components/CATDrawer'));
const MarcaDrawer = lazy(() => import('@/features/suprimentos/components/MarcaDrawer'));
const ProdutoCategoriaDrawer = lazy(() => import('@/features/suprimentos/components/ProdutoCategoriaDrawer'));
const ProdutoSubCategoriaDrawer = lazy(() => import('@/features/suprimentos/components/ProdutoSubCategoriaDrawer'));
const ProdutoTipoDrawer = lazy(() => import('@/features/suprimentos/components/ProdutoTipoDrawer'));
const ProdutoUnidadeMedidaDrawer = lazy(() => import('@/features/suprimentos/components/ProdutoUnidadeMedidaDrawer'));
const ProdutoMargemTipoDrawer = lazy(() => import('@/features/suprimentos/components/ProdutoMargemTipoDrawer'));
const OrdemCompraStatusDrawer = lazy(() => import('@/features/suprimentos/components/OrdemCompraStatusDrawer'));
const VendasDrawer = lazy(() => import('@/features/comercial/components/VendasDrawer'));
const EmpreendimentoDrawer = lazy(() => import('@/features/comercial/components/EmpreendimentoDrawer'));
const OportunidadeStatusDrawer = lazy(() => import('@/features/comercial/components/config/OportunidadeStatusDrawer'));
const ComissaoDetalheDrawer = lazy(() => import('@/features/financeiro/components/ComissaoDetalheDrawer'));
const AtividadeAreaDrawer = lazy(() => import('@/features/atividade/components/AtividadeAreaDrawer'));
const AtividadeTipoDrawer = lazy(() => import('@/features/atividade/components/AtividadeTipoDrawer'));
// Phase 8a — Obra dashboard (8 first-level kinds)
const ObraAmbienteDrawer = lazy(() => import('@/features/obra/components/ObraAmbienteDrawer'));
const ObraAndamentoDrawer = lazy(() => import('@/features/obra/components/ObraAndamentoDrawer'));
const ObraAreaDrawer = lazy(() => import('@/features/obra/components/ObraAreaDrawer'));
const ObraEnvolvidoDrawer = lazy(() => import('@/features/obra/components/ObraEnvolvidoDrawer'));
const ObraPontoDrawer = lazy(() => import('@/features/obra/components/ObraPontoDrawer'));
const ObraProdutoDrawer = lazy(() => import('@/features/obra/components/ObraProdutoDrawer'));
const ObraServicoDrawer = lazy(() => import('@/features/obra/components/ObraServicoDrawer'));
const ObraVistoriaDrawer = lazy(() => import('@/features/obra/components/ObraVistoriaDrawer'));
// Phase 8b — Obra peripherals (5 kinds: tubo/cabo/etapa/arquivo/produto_status)
const ObraTuboDrawer = lazy(() => import('@/features/obra/components/ObraTuboDrawer'));
const ObraCaboDrawer = lazy(() => import('@/features/obra/components/ObraCaboDrawer'));
const ObraEtapaDrawer = lazy(() => import('@/features/obra/components/ObraEtapaDrawer'));
const ObraArquivoDrawer = lazy(() => import('@/features/obra/components/ObraArquivoDrawer'));
const ObraProdutoStatusDrawer = lazy(() => import('@/features/obra/components/ObraProdutoStatusDrawer'));
// Phase 9a — Cadastros leaves (8 kinds: departamento/estado/ambiente_tipo/ponto_tipo_grupo/vistoria_tipo/atraso_motivo/file_categoria/modulo)
const DepartamentoDrawer = lazy(() => import('@/features/cadastro/components/DepartamentoDrawer'));
const EstadoDrawer = lazy(() => import('@/features/cadastro/components/EstadoDrawer'));
const AmbienteTipoDrawer = lazy(() => import('@/features/cadastro/components/AmbienteTipoDrawer'));
const PontoTipoGrupoDrawer = lazy(() => import('@/features/cadastro/components/PontoTipoGrupoDrawer'));
const VistoriaTipoDrawer = lazy(() => import('@/features/cadastro/components/VistoriaTipoDrawer'));
const AtrasoMotivoDrawer = lazy(() => import('@/features/cadastro/components/atraso/AtrasoMotivoDrawer'));
const FileCategoriaDrawer = lazy(() => import('@/features/cadastro/components/FileCategoriaDrawer'));
const ModuloDrawer = lazy(() => import('@/features/cadastro/components/ModuloDrawer'));
// Phase 9b — Cadastros with lookup deps (6 kinds: cidade/ponto_tipo/servico_tipo/profissional_tipo/tipos_cabo/produto)
// Drawers self-fetch lookups via shared React Query hooks (cadastroLookups.queries.ts).
const CidadeDrawer = lazy(() => import('@/features/cadastro/components/CidadeDrawer'));
const FornecedorDrawer = lazy(() => import('@/features/cadastro/components/FornecedorDrawer'));
const PontoTipoDrawer = lazy(() => import('@/features/cadastro/components/PontoTipoDrawer'));
const ServicoTipoDrawer = lazy(() => import('@/features/cadastro/components/ServicoTipoDrawer'));
const ProfissionalTipoDrawer = lazy(() => import('@/features/cadastro/components/profissional/ProfissionalTipoDrawer'));
const TiposCaboDrawer = lazy(() => import('@/features/cadastro/components/TiposCaboDrawer'));
const ProdutoDrawer = lazy(() => import('@/features/cadastro/components/ProdutoDrawer'));
// Phase 10a — Financeiro simple flips (movimento + forma_pagamento). Drawers
// já são standalone-ready: MovimentoDrawer self-fetches movimento_tipo + conta;
// FormaPagamentoDrawer is a leaf CRUD without lookups.
const MovimentoDrawer = lazy(() => import('@/features/financeiro/components/MovimentoDrawer'));
const FormaPagamentoDrawer = lazy(() => import('@/features/financeiro/components/config/FormaPagamentoDrawer'));
// Phase 10b — Lancamento. Drawer self-fetches its 7 lookups via shared React
// Query hooks (financeiroLookups.queries.ts) — standalone-ready for paste-link.
const LancamentoDrawer = lazy(() => import('@/features/financeiro/components/LancamentoDrawer'));
// Phase 11a — Admin/Misc simple flips (4 entity drawers + 1 session drawer).
// Drawers are standalone-ready: each self-fetches its record + lookups (Tarefa
// already born-clean since origin; Perfil/Usuario use feature-local fetches).
// MeuPerfil is a session drawer — derives the entity from useAuth(), no recordId.
const PerfilDrawer = lazy(() => import('@/features/permissions/components/permission/PerfilDrawer'));
const UsuarioDrawer = lazy(() => import('@/features/permissions/components/permission/UsuarioDrawer'));
const TarefaDrawer = lazy(() => import('@/features/tarefas/components/TarefaDrawer'));
const MeuPerfilDrawer = lazy(() => import('@/features/profile/components/MeuPerfilDrawer'));
// Phase 11b — LogPermissao read-only drawer; self-fetches its 4 label-resolution
// lookups via shared React Query hooks (auditoriaLookups.queries.ts).
const LogPermissaoDrawer = lazy(() => import('@/features/auditoria/components/LogPermissaoDrawer'));
// Phase 11c — TransitionRule born-clean drawer; self-fetches configs + perfis
// via shared hooks and lazy-fetches statusOptions per the active config's
// status_table (transitionLookups.queries.ts). Create-mode is rendered locally
// by TransitionRulesManager (transient form, off URL by convention).
const TransitionRuleDrawer = lazy(() => import('@/features/admin/components/config/TransitionRuleDrawer'));

/**
 * Renders the overlay drawer stack on top of the current view. Drawers stack
 * visually (each gets a higher z-index) and conceptually (closing the top one
 * pops the array, revealing whatever was beneath). The stack lives in the
 * NavigationContext and is mirrored to `?drawer=...` so F5 / paste-link
 * restores everything.
 *
 * Each drawer's `onClose` calls `actions.popOverlayDrawer()` so the stack and
 * the URL stay in sync. Drawers that contain links to *other* records
 * (e.g. AtividadeDrawer's `onObraClick`) push onto the stack rather than
 * managing local state, which is what makes the chain participate in the URL.
 *
 * Renderers are looked up via `DRAWER_RENDERERS` keyed by `OverlayDrawerKind`.
 * `null` means "kind reserved for a later phase" — kinds that typecheck via
 * `pushOverlayDrawer(kind, id)` but render nothing yet (with a dev warning).
 * Each phase fills in its own renderers without touching this switch.
 */

/**
 * Context exposed to renderers. Keeps individual drawer files decoupled from
 * `useNavigation` and lets renderers call `pushDrawer` / `popDrawer` /
 * `navigate` to express drawer-on-drawer chains and cross-feature redirects.
 */
type HostContext = {
  key: string;
  hasPermission: HasPermissionFn;
  close: () => void;
  handleModeChange: (mode: string) => void;
  pushDrawer: (kind: OverlayDrawerKind, id: string) => void;
  popDrawer: () => void;
  /**
   * Replace the top stack entry in place — used by sibling navigation
   * (prev/next inside `os_servico`, `os_vistoria`, etc.) so the URL tracks
   * the current sibling without producing a new browser-history entry.
   */
  replaceTop: (kind: OverlayDrawerKind, id: string) => void;
  navigateToView: ReturnType<typeof useNavigation>['actions']['navigate'];
  /**
   * Navigate to a different view while passing a selection patch. Used by
   * `proposta-details`-style hand-offs where the new view needs an entity id
   * (propostaId) to load itself, distinct from generic viewParams.
   */
  navigateWithSelection: ReturnType<typeof useNavigation>['actions']['navigateWithSelection'];
  /**
   * Side-channel that lets a non-stacked view (e.g. `proposta-details`)
   * remember which oportunidade it was opened from, so its own back-link can
   * push that oportunidade drawer back onto the stack.
   */
  setDrawerOportunidadeId: ReturnType<typeof useNavigation>['actions']['setDrawerOportunidadeId'];
};

type DrawerRenderer = (entry: OverlayDrawerState, ctx: HostContext) => ReactNode;

const renderObra: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra', ctx.hasPermission);
  return (
    <ObraDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onPessoaClick={(pessoaId) => ctx.pushDrawer('pessoa', pessoaId)}
      permissions={perms}
    />
  );
};

const renderObraJornada: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_jornada', ctx.hasPermission);
  return (
    <ObraJornadaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProjeto: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('projeto', ctx.hasPermission);
  return (
    <ProjetoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      projeto={null}
      isCreating={false}
      mode={entry.mode}
      onClose={ctx.close}
      onSave={ctx.close}
      onDelete={ctx.close}
      onModeChange={ctx.handleModeChange}
      onNavigateToObraJornada={(obraId, projetoId) => {
        if (!obraId || !projetoId) return;
        // Pop the drawer first, then navigate. ProjetosAtivos's returnState
        // useEffect will push the projeto drawer back on return.
        ctx.popDrawer();
        ctx.navigateToView('obras-jornadas', {
          viewParams: {
            initialFilterValues: { obra_id: [obraId] },
            periodFilter: 'todo-periodo',
            returnState: { drawerOpen: true, selectedProjetoId: projetoId },
          },
        });
      }}
      permissions={perms}
    />
  );
};

const renderAtividade: DrawerRenderer = (entry, ctx) => (
  <AtividadeDrawer
    key={ctx.key}
    isOpen
    eventoId={entry.id}
    onClose={ctx.close}
    onSave={ctx.close}
    onObraClick={(obraId) => ctx.pushDrawer('obra', obraId)}
    onPessoaClick={(payload) => {
      if (payload?.id) ctx.pushDrawer('pessoa', payload.id);
    }}
    onOportunidadeClick={(payload) => {
      if (payload?.id) ctx.pushDrawer('oportunidade', payload.id);
    }}
  />
);

const renderOportunidade: DrawerRenderer = (entry, ctx) => (
  <OportunidadeDrawer
    key={ctx.key}
    isOpen
    oportunidadeId={entry.id}
    onClose={ctx.close}
    onObraClick={(obraId) => ctx.pushDrawer('obra', obraId)}
    onPessoaClick={(pessoaId) => ctx.pushDrawer('pessoa', pessoaId)}
    onAtividadeClick={(id) => ctx.pushDrawer('atividade', id)}
    onPropostaClick={(propostaId) => {
      // proposta is a dedicated view (`/comercial/oportunidades/proposta`),
      // not a drawer kind. Pop the oportunidade drawer first (NAVIGATE does
      // not clear the overlay stack — it would render on top of the new
      // view), then side-channel the parent oportunidade id so the kanban
      // re-pushes the drawer when the user returns.
      ctx.popDrawer();
      ctx.setDrawerOportunidadeId(entry.id);
      ctx.navigateWithSelection('proposta-details', { propostaId });
    }}
  />
);

const renderPessoa: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('pessoa', ctx.hasPermission);
  return (
    <PessoaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderRequisicao: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('requisicao', ctx.hasPermission);
  return (
    <RequisicaoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onAutoOpenSeparacaoConsumed={() => {}}
      permissions={perms}
    />
  );
};

const renderOrdemCompra: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('ordem_compra', ctx.hasPermission);
  return (
    <OrdemCompraDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onStatusChange={() => {}}
      onOrcamentoClick={(orcamentoId) => ctx.pushDrawer('orcamento', orcamentoId)}
      permissions={perms}
    />
  );
};

const renderOs: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('os', ctx.hasPermission);
  return (
    <OsDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onServicoClick={(servicoId) => ctx.pushDrawer('os_servico', servicoId)}
      onVistoriaClick={(vistoriaId) => ctx.pushDrawer('os_vistoria', vistoriaId)}
      onReportReviewClick={(osId) => ctx.pushDrawer('os_report_review', osId)}
      onOsClick={(osId) => ctx.pushDrawer('os', osId)}
      permissions={perms}
    />
  );
};

const renderOsServico: DrawerRenderer = (entry, ctx) => (
  <OsServicoDrawer
    key={ctx.key}
    isOpen
    osServicoId={entry.id}
    onClose={ctx.close}
    onNavigate={(servico) => {
      const id = (servico as { id?: unknown } | null | undefined)?.id;
      if (typeof id === 'string' && id) ctx.replaceTop('os_servico', id);
    }}
  />
);

const renderOsVistoria: DrawerRenderer = (entry, ctx) => (
  <OsVistoriaDrawer
    key={ctx.key}
    isOpen
    osVistoriaId={entry.id}
    onClose={ctx.close}
    onNavigate={(vistoria) => {
      const id = (vistoria as { id?: unknown } | null | undefined)?.id;
      if (typeof id === 'string' && id) ctx.replaceTop('os_vistoria', id);
    }}
  />
);

const renderOsRequisicao: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('os_requisicao', ctx.hasPermission);
  return (
    <OsRequisicaoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderOsReportReview: DrawerRenderer = (entry, ctx) => (
  <OsReportReviewDrawer
    key={ctx.key}
    isOpen
    osId={entry.id}
    onClose={ctx.close}
    onSave={ctx.close}
  />
);

const renderOrcamento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('orcamento', ctx.hasPermission);
  // Standalone (sem OC parent): forçar view-only via ocLocked. OrcamentoDrawer
  // já trata ocLocked desabilitando edit/save/delete (cf. OrcamentoDrawer.tsx
  // L586/L597/L599/L601). Editar requer reabrir a OC.
  return (
    <OrcamentoDrawer
      key={ctx.key}
      isOpen
      ocId={null}
      orcamentoId={entry.id}
      onClose={ctx.close}
      ocLocked
      permissions={perms}
    />
  );
};

const renderMovimentacaoEstoque: DrawerRenderer = (entry, ctx) => {
  // MovimentacaoEstoqueDrawer trava canEdit/canDelete como false internamente
  // (movimentos são criados via trigger / fluxo OC; UI é view-only). Por isso
  // não recebe `permissions` aqui — a entry em DRAWER_PERMISSIONS serve de
  // contrato para futuras iterações que liberem edição.
  return (
    <MovimentacaoEstoqueDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onSave={ctx.close}
      onRequisicaoClick={(reqId) => ctx.pushDrawer('requisicao', reqId)}
      onOrdemCompraClick={(ocId) => ctx.pushDrawer('ordem_compra', ocId)}
    />
  );
};

const renderCat: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('cat', ctx.hasPermission);
  return (
    <CATDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode ?? 'view'}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Suprimentos cadastros (Phase 5b) — todos os 7 são CRUDs simples baseados em
// GenericDrawer. View-mode chega aqui via centralDrawerKind no GenericList do
// parent; create-mode permanece local-state no GenericList por convenção
// (transient form, off URL).

const renderMarca: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('marca', ctx.hasPermission);
  return (
    <MarcaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProdutoCategoria: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('produto_categoria', ctx.hasPermission);
  return (
    <ProdutoCategoriaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProdutoSubCategoria: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('produto_subcategoria', ctx.hasPermission);
  return (
    <ProdutoSubCategoriaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProdutoTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('produto_tipo', ctx.hasPermission);
  return (
    <ProdutoTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProdutoUnidadeMedida: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('produto_unidade_medida', ctx.hasPermission);
  return (
    <ProdutoUnidadeMedidaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProdutoMargemTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('produto_margem_tipo', ctx.hasPermission);
  return (
    <ProdutoMargemTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// OrdemCompraStatusDrawer não consome um objeto `permissions`: aceita
// canEdit/canDelete diretos (cf. OrdemCompraStatusDrawer.tsx L40-41) e ainda
// reavalia internamente via getPermissionScope para registros globais
// (tenant_id null) — escopo platform.
const renderOrdemCompraStatus: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('ordem_compra_status', ctx.hasPermission);
  return (
    <OrdemCompraStatusDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      canEdit={perms.canEdit}
      canDelete={perms.canDelete}
    />
  );
};

// Phase 6 — Comercial cadastros
//
// Vendas é view-only: o drawer ignora os 3 lookups que historicamente eram
// passados via `drawerProps` (oportunidadeTipos/oportunidadeOrigens/
// consultores) — props existem para satisfazer GenericList mas o renderer
// view-only não os consome (cf. VendasDrawer L159 + L185).
const renderVendas: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('vendas', ctx.hasPermission);
  return (
    <VendasDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      onClose={ctx.close}
      permissions={perms}
    />
  );
};

const renderEmpreendimento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('empreendimento', ctx.hasPermission);
  return (
    <EmpreendimentoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onPessoaClick={(pessoaId) => ctx.pushDrawer('pessoa', pessoaId)}
      onObraClick={(obraId) => ctx.pushDrawer('obra', obraId)}
      permissions={perms}
    />
  );
};

// OportunidadeStatusDrawer (cf. config/OportunidadeStatusDrawer.tsx L36-37) —
// mesmo padrão divergente do OrdemCompraStatusDrawer: aceita canEdit/canDelete
// diretos (não objeto `permissions`) e reavalia getPermissionScope internamente
// para registros globais (tenant_id null).
const renderOportunidadeStatus: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('oportunidade_status', ctx.hasPermission);
  return (
    <OportunidadeStatusDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      canEdit={perms.canEdit}
      canDelete={perms.canDelete}
    />
  );
};

// Phase 10c — ComissaoDetalheDrawer absorbeu self-fetch + canApprove (via
// useAuth) + ConfirmActionModal + RPC fn_fechar_comissao. Standard Marca-pattern.
// Wrapper transitional removido; Comissao.tsx (list) migrado para central
// drawer com useDrawerCloseEffect → list refaz fetch após close.
const renderComissaoDetalhe: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('comissao_detalhe', ctx.hasPermission);
  return (
    <ComissaoDetalheDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Phase 7 — Atividade cadastros
//
// AtividadeAreaDrawer / AtividadeTipoDrawer são CRUDs simples baseados em
// GenericDrawer (mesmo pattern dos cadastros Suprimentos da 5b). View-mode
// chega aqui via centralDrawerKind no GenericList; create-mode permanece
// local-state no parent por convenção (transient form, off URL).

const renderAtividadeArea: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('atividade_area', ctx.hasPermission);
  return (
    <AtividadeAreaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderAtividadeTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('atividade_tipo', ctx.hasPermission);
  return (
    <AtividadeTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// ── Phase 8a — Obra dashboard ────────────────────────────────────────────
// 8 first-level kinds. Born-clean: drawers receive `recordId` only and
// derive `obra_id` from the loaded record via FK joins (e.g. ObraAndamento
// reads `obra_id` from `getObraAndamentoWithRelations`). No parent context
// flows through props; standalone-parity comes for free via paste-link/F5.

const renderObraAmbiente: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_ambiente', ctx.hasPermission);
  return (
    <ObraAmbienteDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraAndamento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_andamento', ctx.hasPermission);
  return (
    <ObraAndamentoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onServicoClick={(servicoId) => ctx.pushDrawer('obra_servico', servicoId)}
      permissions={perms}
    />
  );
};

const renderObraArea: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_area', ctx.hasPermission);
  return (
    <ObraAreaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraEnvolvido: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_envolvido', ctx.hasPermission);
  return (
    <ObraEnvolvidoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraPonto: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_ponto', ctx.hasPermission);
  return (
    <ObraPontoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onProdutoClick={(produtoId) => ctx.pushDrawer('obra_produto', produtoId)}
      onServicoClick={(servicoId) => ctx.pushDrawer('obra_servico', servicoId)}
      permissions={perms}
    />
  );
};

const renderObraProduto: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_produto', ctx.hasPermission);
  return (
    <ObraProdutoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onOrdemCompraClick={(ocId) => ctx.pushDrawer('ordem_compra', ocId)}
      onRequisicaoClick={(reqId) => ctx.pushDrawer('requisicao', reqId)}
      permissions={perms}
    />
  );
};

const renderObraServico: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_servico', ctx.hasPermission);
  return (
    <ObraServicoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onOsClick={(osId) => ctx.pushDrawer('os', osId)}
      permissions={perms}
    />
  );
};

const renderObraVistoria: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_vistoria', ctx.hasPermission);
  return (
    <ObraVistoriaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onOsClick={(osId) => ctx.pushDrawer('os', osId)}
      permissions={perms}
    />
  );
};

const renderObraTubo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_tubo', ctx.hasPermission);
  return (
    <ObraTuboDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraCabo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_cabo', ctx.hasPermission);
  return (
    <ObraCaboDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraEtapa: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_etapa', ctx.hasPermission);
  return (
    <ObraEtapaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraArquivo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_arquivo', ctx.hasPermission);
  return (
    <ObraArquivoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      onClose={ctx.close}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderObraProdutoStatus: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('obra_produto_status', ctx.hasPermission);
  return (
    <ObraProdutoStatusDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Phase 9a — Cadastros leaves (Marca-pattern; standard `permissions` prop).

const renderDepartamento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('departamento', ctx.hasPermission);
  return (
    <DepartamentoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderEstado: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('estado', ctx.hasPermission);
  return (
    <EstadoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderAmbienteTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('ambiente_tipo', ctx.hasPermission);
  return (
    <AmbienteTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderPontoTipoGrupo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('ponto_tipo_grupo', ctx.hasPermission);
  return (
    <PontoTipoGrupoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderVistoriaTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('vistoria_tipo', ctx.hasPermission);
  return (
    <VistoriaTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderAtrasoMotivo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('atraso_motivo', ctx.hasPermission);
  return (
    <AtrasoMotivoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderFileCategoria: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('file_categoria', ctx.hasPermission);
  return (
    <FileCategoriaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderModulo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('modulo', ctx.hasPermission);
  return (
    <ModuloDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Phase 9b — Cadastros with lookup deps (drawers self-fetch via React Query).

const renderCidade: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('cidade', ctx.hasPermission);
  return (
    <CidadeDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderFornecedor: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('fornecedor', ctx.hasPermission);
  return (
    <FornecedorDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderPontoTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('ponto_tipo', ctx.hasPermission);
  return (
    <PontoTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderServicoTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('servico_tipo', ctx.hasPermission);
  return (
    <ServicoTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProfissionalTipo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('profissional_tipo', ctx.hasPermission);
  return (
    <ProfissionalTipoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderTiposCabo: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('tipos_cabo', ctx.hasPermission);
  return (
    <TiposCaboDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderProduto: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('produto', ctx.hasPermission);
  return (
    <ProdutoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      onOrdemCompraClick={(id) => ctx.pushDrawer('ordem_compra', id)}
      onRequisicaoClick={(id) => ctx.pushDrawer('requisicao', id)}
      permissions={perms}
    />
  );
};

// Phase 10a — Financeiro simple flips
//
// MovimentoDrawer / FormaPagamentoDrawer são CRUDs standard (Marca-pattern).
// MovimentoDrawer self-fetches `movimento_tipo` + `conta` ao montar; create-mode
// permanece local-state no GenericList por convenção (transient form, off URL).

const renderMovimento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('movimento', ctx.hasPermission);
  return (
    <MovimentoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderFormaPagamento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('forma_pagamento', ctx.hasPermission);
  return (
    <FormaPagamentoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Phase 10b — Lancamento. Standard Marca-pattern; drawer self-fetches its 7
// lookups (partes/contas/categorias/centros_custo/propostas/formas_pagamento/
// tipos_movimento) via shared React Query hooks. Create-mode permanece local
// no GenericList por convenção.
const renderLancamento: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('lancamento', ctx.hasPermission);
  return (
    <LancamentoDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Phase 11a — Admin/Misc simple flips
//
// Standard Marca-pattern for the 3 entity drawers (perfil/usuario/tarefa).
// Lookups: TarefaDrawer self-fetches its 7 lookups since origin (born-clean);
// PerfilDrawer is a leaf CRUD; UsuarioDrawer self-fetches perfis + departamentos
// internally (the `auxiliaryData={{ perfil }}` on the list is for list-side
// advanced filters, not the drawer).

const renderPerfil: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('perfil', ctx.hasPermission);
  return (
    <PerfilDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

const renderUsuario: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('usuario', ctx.hasPermission);
  return (
    <UsuarioDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      permissions={perms}
    />
  );
};

const renderTarefa: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('tarefa', ctx.hasPermission);
  return (
    <TarefaDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={ctx.handleModeChange}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={perms}
    />
  );
};

// Session drawer — entity is the logged-in user (derived from useAuth inside
// the drawer). The renderer ignores `entry.id` (always empty for session
// kinds) and `entry.mode` (drawer manages its own view/edit state internally).
const renderMeuPerfil: DrawerRenderer = (_entry, ctx) => (
  <MeuPerfilDrawer key={ctx.key} isOpen onClose={ctx.close} />
);

// Phase 11b — LogPermissaoDrawer is read-only (no save/delete/mode). The
// renderer passes only `recordId` + `onClose`; the drawer self-fetches the
// log record AND the 4 label-resolution lookups via shared React Query hooks.
// No `permissions` prop — read access is gated tela-level by AUDITORIA.
const renderLogPermissao: DrawerRenderer = (entry, ctx) => (
  <LogPermissaoDrawer key={ctx.key} isOpen recordId={entry.id} onClose={ctx.close} />
);

// Phase 11c — TransitionRuleDrawer born-clean. Self-fetches configs/perfis +
// lazy statusOptions per the loaded rule's config. Mode union is strict
// ('view' | 'edit' | 'create'), so we coerce the host's setOverlayDrawerMode
// dispatch (which only emits 'view' | 'edit') through the existing handler.
const renderTransitionRule: DrawerRenderer = (entry, ctx) => {
  const perms = resolveDrawerPermissions('transition_rule', ctx.hasPermission);
  return (
    <TransitionRuleDrawer
      key={ctx.key}
      isOpen
      recordId={entry.id}
      mode={entry.mode}
      isCreating={false}
      onClose={ctx.close}
      onModeChange={(m) => ctx.handleModeChange(m === 'create' ? 'edit' : m)}
      onSave={ctx.close}
      onDelete={ctx.close}
      permissions={{ canEdit: perms.canEdit, canDelete: perms.canDelete }}
    />
  );
};

/**
 * Renderer registry. Adding a new `OverlayDrawerKind` is a typecheck error
 * here until a renderer (or explicit `null`) is provided — the Record key
 * exhaustiveness keeps the host honest as the union grows.
 *
 * `null` entries are kinds reserved for a later phase: the type accepts the
 * kind, but the host renders nothing and emits a dev warning so unintentional
 * pushes get noticed before the renderer ships.
 */
const DRAWER_RENDERERS: Record<OverlayDrawerKind, DrawerRenderer | null> = {
  // Phase 1 — implemented
  obra: renderObra,
  jornada: renderObraJornada,
  obra_jornada: renderObraJornada,
  projeto: renderProjeto,
  atividade: renderAtividade,
  oportunidade: renderOportunidade,
  requisicao: renderRequisicao,
  ordem_compra: renderOrdemCompra,

  // Phase 3 — Pessoa
  pessoa: renderPessoa,

  // Phase 4 — OS
  os: renderOs,
  os_servico: renderOsServico,
  os_vistoria: renderOsVistoria,
  os_report_review: renderOsReportReview,
  // View-mode of an OS-linked requisicao goes to URL via this kind. Create
  // flow (no requisicao yet) stays local in OsDrawer — the create modal
  // collects products before producing a record, so it's a transient form
  // (per convention). Once created, OsDrawer pushes this kind in 'edit' mode.
  os_requisicao: renderOsRequisicao,

  // Phase 5 — Suprimentos
  marca: renderMarca,
  produto_categoria: renderProdutoCategoria,
  produto_subcategoria: renderProdutoSubCategoria,
  produto_tipo: renderProdutoTipo,
  produto_unidade_medida: renderProdutoUnidadeMedida,
  produto_margem_tipo: renderProdutoMargemTipo,
  movimentacao_estoque: renderMovimentacaoEstoque,
  orcamento: renderOrcamento,
  cat: renderCat,
  ordem_compra_status: renderOrdemCompraStatus,

  // Phase 6 — Comercial
  vendas: renderVendas,
  // The portrait renderer arrives with the judgment screen. Until then a
  // push renders nothing.
  proposta_resultado: null,
  empreendimento: renderEmpreendimento,
  oportunidade_status: renderOportunidadeStatus,
  comissao_detalhe: renderComissaoDetalhe,

  // Phase 7 — Atividade
  atividade_area: renderAtividadeArea,
  atividade_tipo: renderAtividadeTipo,

  // Phase 8 — Obra dashboard
  obra_ambiente: renderObraAmbiente,
  obra_andamento: renderObraAndamento,
  obra_area: renderObraArea,
  obra_envolvido: renderObraEnvolvido,
  obra_ponto: renderObraPonto,
  obra_produto: renderObraProduto,
  obra_servico: renderObraServico,
  obra_vistoria: renderObraVistoria,
  obra_tubo: renderObraTubo,
  obra_cabo: renderObraCabo,
  obra_etapa: renderObraEtapa,
  obra_arquivo: renderObraArquivo,
  obra_produto_status: renderObraProdutoStatus,

  // Phase 9a — Cadastros leaves
  departamento: renderDepartamento,
  estado: renderEstado,
  ambiente_tipo: renderAmbienteTipo,
  ponto_tipo_grupo: renderPontoTipoGrupo,
  vistoria_tipo: renderVistoriaTipo,
  atraso_motivo: renderAtrasoMotivo,
  file_categoria: renderFileCategoria,
  modulo: renderModulo,

  // Phase 9b — Cadastros with lookup deps
  cidade: renderCidade,
  fornecedor: renderFornecedor,
  ponto_tipo: renderPontoTipo,
  servico_tipo: renderServicoTipo,
  profissional_tipo: renderProfissionalTipo,
  tipos_cabo: renderTiposCabo,
  produto: renderProduto,

  // Phase 10 — Financeiro
  lancamento: renderLancamento,
  movimento: renderMovimento,
  forma_pagamento: renderFormaPagamento,

  // Phase 11a — Admin/Misc simple flips + session drawer
  perfil: renderPerfil,
  usuario: renderUsuario,
  tarefa: renderTarefa,
  meu_perfil: renderMeuPerfil,
  // Phase 11b — LogPermissao read-only drawer with self-fetched lookups
  log_permissao: renderLogPermissao,
  // Phase 11c — TransitionRule born-clean (drawer self-fetches all lookups)
  transition_rule: renderTransitionRule,
};

/**
 * Kinds whose stack id is the record the app opens for the gated recurso
 * (`joshua://os/<id>`, `joshua://requisicao/<id>`). Other kinds (os_servico,
 * os_vistoria) hold a child id, so their notice opens the app's start screen.
 */
const APP_RECORD_KINDS: ReadonlySet<OverlayDrawerKind> = new Set<OverlayDrawerKind>([
  'os',
  'os_report_review',
  'requisicao',
  'os_requisicao',
]);

/**
 * Channel decision for one stack entry (JOSH-91). `recurso` is the gated
 * recurso, `bloqueado` whether its view is on the other channel only.
 */
type CanalDecision = { entry: OverlayDrawerState; recurso: string | null; bloqueado: boolean };

/** One-shot dev warnings — avoid spamming for every render of an unrendered kind. */
const warnedUnrenderedKinds = new Set<OverlayDrawerKind>();

export function OverlayDrawerHost() {
  const { overlayDrawerStack, actions } = useNavigation();
  const { hasPermission, temVisualizarEmOutroCanal, loading, loadingStage, pessoa } = useAuth();

  // JOSH-91 channel gate. The host re-renders every entry with the live
  // permission cache, so the decision is kept per stack entry: a drawer already
  // open is never torn down by a later cache refresh. It is recorded only once
  // permissions finished loading; until then a gated entry renders the loading
  // shell, so a cold `?drawer=` load never freezes as "not blocked".
  const permissoesProntas = !loading && loadingStage === null && pessoa !== null;
  const decisionsRef = useRef<Array<CanalDecision | null>>([]);
  const decisions: Array<CanalDecision | null> = overlayDrawerStack.map((entry, index) => {
    // Entries are immutable objects created per push. A mode toggle or URL
    // sync rebuilds the object for the same kind+id at the same position, so
    // that keeps its decision too. A pop commits a shorter stack first, which
    // drops the decision, so a later push of the same drawer is re-evaluated.
    const prev = decisionsRef.current[index];
    if (prev && (prev.entry === entry || (prev.entry.kind === entry.kind && prev.entry.id === entry.id))) {
      return prev.entry === entry ? prev : { ...prev, entry };
    }
    const recurso = resolveCanalRecurso(entry.kind);
    if (!recurso) return { entry, recurso: null, bloqueado: false };
    if (!permissoesProntas) return null;
    return { entry, recurso, bloqueado: temVisualizarEmOutroCanal(recurso) };
  });
  // Committed decisions only: a discarded (transition) render never stores one.
  useLayoutEffect(() => {
    decisionsRef.current = decisions;
  });

  if (overlayDrawerStack.length === 0) return null;

  // Narrow-at-the-boundary adapter: child drawers type onModeChange as
  // (mode: string) => void, but our state uses the strict 'view' | 'edit' union.
  const handleModeChange = (mode: string) => {
    if (mode === 'view' || mode === 'edit') actions.setOverlayDrawerMode(mode);
  };

  const renderEntry = (entry: OverlayDrawerState, index: number) => {
    // Stable key includes index so re-pushing the same kind twice (rare but
    // possible: obra A → obra B drill) doesn't collapse into a single instance.
    // Visual stacking comes from DOM order — drawers portal to body in the
    // order React mounts them, and same-z-index siblings tie-break by DOM
    // position (later wins). The pre-existing local-state pattern already
    // relies on this behavior.
    const key = `${entry.kind}-${index}-${entry.id}`;
    const zIndex = 1000 + index * 100;

    const decision = decisions[index];
    if (!decision || decision.bloqueado) {
      return (
        <AvisoOutroCanalDrawer
          key={`${key}-aviso-canal`}
          recurso={decision?.recurso ?? null}
          registroId={APP_RECORD_KINDS.has(entry.kind) ? entry.id : null}
          carregando={!decision}
          onClose={() => actions.popOverlayDrawer()}
          zIndex={zIndex}
        />
      );
    }

    const renderer = DRAWER_RENDERERS[entry.kind];

    if (!renderer) {
      if (import.meta.env.DEV && !warnedUnrenderedKinds.has(entry.kind)) {
        warnedUnrenderedKinds.add(entry.kind);
        // eslint-disable-next-line no-console
        console.warn(
          `[OverlayDrawerHost] No renderer for kind "${entry.kind}". ` +
            `It typechecks but renders nothing — register it in DRAWER_RENDERERS ` +
            `when the matching phase ships.`,
        );
      }
      return null;
    }

    const element = renderer(entry, {
      key,
      hasPermission,
      close: () => actions.popOverlayDrawer(),
      handleModeChange,
      pushDrawer: (kind, id) => actions.pushOverlayDrawer(kind, id),
      popDrawer: () => actions.popOverlayDrawer(),
      replaceTop: (kind, id) => actions.replaceTopOverlayDrawer(kind, id),
      navigateToView: actions.navigate,
      navigateWithSelection: actions.navigateWithSelection,
      setDrawerOportunidadeId: actions.setDrawerOportunidadeId,
    });

    // Stack-position-based z-index. Each drawer's own default zIndex varies
    // (OsDrawer=10000, GenericDrawer=1000, etc.), so DOM mount order alone
    // doesn't guarantee the top stack entry paints on top — a drawer with a
    // lower zIndex pushed last would still render below an earlier drawer
    // with a higher default. Inject zIndex per stack position so layering
    // always matches stack order. Step 100 leaves room for each drawer's
    // internal layers (overlay + panel + nested popovers up to ~+10).
    if (!element || !isValidElement(element)) return element;
    return cloneElement(element as ReactElement<{ zIndex?: number }>, {
      zIndex,
    });
  };

  // Single top-level Suspense ensures the stack commits atomically in JSX
  // order — DOM order then matches stack order regardless of which lazy chunk
  // resolves first. Critical for F5/paste-link of a multi-drawer URL where
  // chunks race and same-z-index drawers tie-break by DOM position.
  //
  // To avoid the "all open drawers flicker to null while a new chunk loads"
  // bug when pushing in-session, push/replace dispatches are marked as React
  // transitions in `use-navigation.ts`. Transitions keep the previously
  // committed tree visible until the new tree's chunks resolve, then commit
  // atomically — no fallback flash for already-mounted drawers.
  return (
    <Suspense fallback={null}>{overlayDrawerStack.map(renderEntry)}</Suspense>
  );
}
